import { Attribute, AutocompleteType, Station } from "api"
import { StationMapRequest } from "lib/api"
import { ReactNode, createContext, useEffect, useState } from "react"

interface SelectedInput {
  id: number
  name?: string
  type: AutocompleteType
  geoLocation?: {
    latitude: number
    longitude: number
  }
}

interface ContextType {
  selectedLocation: Station | null
  setSelectedLocation: React.Dispatch<React.SetStateAction<Station | null>>
  selectedCity: SelectedInput | null
  setSelectedCity: React.Dispatch<React.SetStateAction<SelectedInput | null>>
  showAsList: boolean
  setShowAsList: React.Dispatch<React.SetStateAction<boolean>>
  searchValue: string
  setSearchValue: React.Dispatch<React.SetStateAction<string>>
  filters: Attribute[] | null
  updateFilters: (attributes: Attribute[]) => void
  mobileDetails: boolean
  setMobileDetails: React.Dispatch<React.SetStateAction<boolean>>
  setQueryParams: () => void
  queryParams: StationMapRequest | null
  resetFilters: () => void
  initialValueFilters: Attribute[] | null
  isMapLoaded?: boolean,
  setIsMapLoaded: React.Dispatch<React.SetStateAction<boolean>>
}

// Initialize the context with a default value
const defaultState: ContextType = {
  selectedCity: null,
  setSelectedCity: () => { },
  selectedLocation: null,
  setSelectedLocation: () => { },
  showAsList: false,
  setShowAsList: () => { },
  searchValue: "",
  setSearchValue: () => { },
  filters: null,
  updateFilters: () => [],
  mobileDetails: false,
  setMobileDetails: () => { },
  setQueryParams: () => { },
  queryParams: null,
  resetFilters: () => { },
  initialValueFilters: null,
  isMapLoaded: false,
  setIsMapLoaded: () => { }
}

export const LocationContext = createContext<ContextType>(defaultState)

interface Props {
  children: ReactNode,
  metadata: Attribute[]
  defaultFilterIds?: number[] | null
}

export const LocationProvider: React.FC<Props> = ({ children, metadata, defaultFilterIds }) => {

  const [selectedLocation, setSelectedLocation] = useState<Station | null>(null)
  const [searchValue, setSearchValue] = useState<string>("") // New state for the search input
  const [selectedCity, setSelectedCity] = useState<SelectedInput | null>(null)

  const [showAsList, setShowAsList] = useState<boolean>(false)
  const [mobileDetails, setMobileDetails] = useState<boolean>(false)
  // New state for filter selections
  const initialValueFilters = (metadata && defaultFilterIds) ? metadata.filter(meta => defaultFilterIds.includes(meta.id)) : null
  const [filters, setFilters] = useState<Attribute[] | null>(initialValueFilters)
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false)


  // Update filters when defaultFilterIds changes
  useEffect(() => {
    if (defaultFilterIds && metadata) {
      const updatedFilters = metadata.filter(meta => defaultFilterIds.includes(meta.id))
      setFilters(updatedFilters)
    }
  }, [defaultFilterIds, metadata])

  useEffect(() => {
    if (!isMapLoaded) {
      return
    }
    setQueryParams()
  }, [selectedCity, selectedLocation, filters, searchValue])
  // Method to update filters
  const updateFilters = (attributes: Attribute[]) => {
    setFilters(attributes)
  }

  const resetFilters = () => {
    setFilters([...initialValueFilters || []])
  }

  const setQueryParams = () => {
    const currentParams = new URLSearchParams(window.location.search)
    const queryParams = new URLSearchParams(currentParams)
    queryParams.delete("search")
    queryParams.delete("stationid")
    queryParams.delete("station")
    queryParams.delete("metadata")
    queryParams.delete("cityid")


    if (filters && filters?.length > 0) {
      const arr = JSON.stringify(filters.map((el) => {
        return el.id
      }))
      queryParams.set("metadata", arr)
    }
    if (selectedCity?.name) {
      queryParams.set("search", selectedCity.name)
    }
    if (selectedCity?.type === AutocompleteType.Station) {
      queryParams.set("stationid", selectedCity.id.toString())
    }
    if (selectedLocation?.id) {
      queryParams.set("station", selectedLocation.id.toString())
    }

    const url = new URL(window.location.href)
    url.search = queryParams.toString()
    window.history.replaceState(null, "", url.toString())
  }

  const queryParams: StationMapRequest = {
    MetaData: filters?.map((el) => el.id) || [],
    CityId:
      selectedCity?.type === AutocompleteType.Station
        ? undefined
        : selectedCity?.id,
    LocationLat: selectedCity?.geoLocation?.latitude!,
    LocationLng: selectedCity?.geoLocation?.longitude!,
  }


  return (
    <LocationContext.Provider
      value={{
        selectedLocation,
        setSelectedLocation,
        showAsList,
        setShowAsList,
        searchValue,
        setSearchValue,
        filters,
        updateFilters,
        selectedCity,
        setSelectedCity,
        mobileDetails,
        setMobileDetails,
        setQueryParams,
        resetFilters,
        queryParams,
        initialValueFilters,
        isMapLoaded,
        setIsMapLoaded
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}
